/**
 * EmbeddedCategoryManager Component
 *
 * Embedded category manager for use within content editors.
 * Manages categories for a specific type (portfolio or shop).
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import type { Category } from '../../services/api';
import api from '../../services/api';

import styles from './EmbeddedCategoryManager.module.css';

/**
 * Category form data
 */
type CategoryFormData = {
  readonly name: string;
  readonly slug: string;
  readonly description: string;
  readonly order: number;
  readonly enabled: boolean;
};

/**
 * Helper function to extract error message
 * @param err
 * @param fallback
 */
const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { readonly message: string }).message);
  }
  return fallback;
};

/**
 * EmbeddedCategoryManager Props
 */
type EmbeddedCategoryManagerProps = {
  readonly type: 'portfolio' | 'shop';
};

const EmbeddedCategoryManager: React.FC<EmbeddedCategoryManagerProps> = ({ type }) => {
  // State
  const [categories, setCategories] = useState<ReadonlyArray<Category>>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    slug: '',
    description: '',
    order: 1,
    enabled: true,
  });

  // Load categories on mount
  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Auto-generate slug when name changes
  useEffect(() => {
    if (!editing && formData.name) {
      setFormData((prev) => ({
        ...prev,
        slug: generateSlug(formData.name),
      }));
    }
  }, [formData.name, editing]);

  /**
   * Load categories from API
   */
  const loadCategories = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await api.categories.getByType(type);
      setCategories(response.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate URL-friendly slug from name
   * @param name
   */
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  /**
   * Handle add new category
   */
  const handleAdd = (): void => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      order: categories.length + 1,
      enabled: true,
    });
    setEditing(null);
    setShowForm(true);
  };

  /**
   * Handle edit category
   * @param category
   */
  const handleEdit = (category: Category): void => {
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description ?? '',
      order: category.order,
      enabled: category.enabled,
    });
    setEditing(category);
    setShowForm(true);
  };

  /**
   * Handle save (create or update)
   */
  const handleSave = async (): Promise<void> => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    if (!formData.slug.trim()) {
      toast.error('Category slug is required');
      return;
    }
    if (!formData.order || formData.order < 1) {
      toast.error('Order must be at least 1');
      return;
    }

    const dataToSave = {
      ...formData,
      type,
      order: Math.max(1, parseInt(String(formData.order)) || 1),
    };

    try {
      if (editing) {
        await api.categories.update(editing._id, dataToSave);
        toast.success('Category updated successfully');
      } else {
        await api.categories.create(dataToSave);
        toast.success('Category created successfully');
      }

      setShowForm(false);
      setEditing(null);
      await loadCategories();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to save category');
      toast.error(message);
      console.error('Save error:', error);
    }
  };

  /**
   * Handle delete category
   * @param id
   * @param name
   */
  const handleDelete = async (id: string, name: string): Promise<void> => {
    if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.categories.delete(id);
      toast.success('Category deleted successfully');
      await loadCategories();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to delete category');
      toast.error(message);
      console.error('Delete error:', error);
    }
  };

  /**
   * Handle cancel form
   */
  const handleCancel = (): void => {
    setShowForm(false);
    setEditing(null);
  };

  /**
   * Handle form field change
   * @param field
   * @param value
   */
  const handleChange = (field: keyof CategoryFormData, value: string | number | boolean): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className={styles.embeddedCategoryManager}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Categories</h3>
          <p className={styles.subtitle}>Manage {type} categories for filtering images</p>
        </div>
        <button onClick={handleAdd} className={styles.addButton}>
          + Add Category
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm ? (
        <div className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Category' : 'Add New Category'}</h4>

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="name">Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter category name"
                maxLength={50}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="slug">Slug *</label>
              <input
                id="slug"
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="auto-generated"
                maxLength={50}
              />
              <small>URL-friendly identifier</small>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="order">Order</label>
              <input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
                <span>Enabled</span>
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows={2}
              maxLength={500}
            />
          </div>

          <div className={styles.formActions}>
            <button onClick={handleCancel} className={styles.cancelButton}>
              Cancel
            </button>
            <button onClick={handleSave} className={styles.saveButton}>
              {editing ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      ) : null}

      {/* Categories List */}
      {loading ? (
        <div className={styles.loading}>Loading categories...</div>
      ) : categories.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No categories yet. Click "Add Category" to create one.</p>
        </div>
      ) : (
        <div className={styles.categoriesGrid}>
          {categories.map((category) => (
            <div key={category._id} className={styles.categoryCard}>
              <div className={styles.categoryHeader}>
                <div>
                  <h4 className={styles.categoryName}>{category.name}</h4>
                  <p className={styles.categorySlug}>{category.slug}</p>
                </div>
                <span className={styles.categoryOrder}>#{category.order}</span>
              </div>

              {category.description ? (
                <p className={styles.categoryDescription}>{category.description}</p>
              ) : null}

              <div className={styles.categoryFooter}>
                <span className={category.enabled ? styles.statusEnabled : styles.statusDisabled}>
                  {category.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <div className={styles.categoryActions}>
                  <button
                    onClick={() => handleEdit(category)}
                    className={styles.editButton}
                    title="Edit category"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(category._id, category.name)}
                    className={styles.deleteButton}
                    title="Delete category"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmbeddedCategoryManager;
